import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubdistrictSearch({ value, label: selectedLabel, onChange, placeholder = "Type kecamatan name...", className = "" }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearch = (q) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/shipping/search-subdistrict`, { params: { q } });
        setResults(res.data || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleSelect = (item) => {
    onChange({ id: item.id, label: item.label, city: item.city_name, province: item.province_name });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onChange({ id: null, label: '', city: '', province: '' });
    setQuery('');
  };

  const formatLabel = (label) => {
    return label.split(', ').map(s => s.charAt(0) + s.slice(1).toLowerCase()).join(', ');
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      {value && selectedLabel ? (
        <div className="flex items-center gap-2 bg-cream-50 border border-cream-100 rounded-xl px-4 py-3">
          <MapPin size={14} className="text-[#C5A880] flex-shrink-0" />
          <span className="font-body text-sm text-[#2C2A29] flex-1 truncate" data-testid="subdistrict-selected">
            {formatLabel(selectedLabel)}
          </span>
          <button type="button" onClick={handleClear} className="text-[#A89F91] hover:text-[#2C2A29] flex-shrink-0" data-testid="subdistrict-clear">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => { if (results.length > 0) setOpen(true); }}
            className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent pr-10"
            placeholder={placeholder}
            data-testid="subdistrict-input"
          />
          {searching && (
            <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#A89F91]" />
          )}
        </div>
      )}

      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-cream-100 rounded-xl shadow-lg max-h-60 overflow-y-auto" data-testid="subdistrict-dropdown">
          {results.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full text-left px-4 py-2.5 hover:bg-[#FFF5F5] transition-colors flex items-start gap-2 border-b border-cream-50 last:border-0"
              data-testid={`subdistrict-option-${item.id}`}
            >
              <MapPin size={12} className="text-[#C5A880] mt-0.5 flex-shrink-0" />
              <span className="font-body text-sm text-[#2C2A29]">{formatLabel(item.label)}</span>
            </button>
          ))}
        </div>
      )}

      {open && results.length === 0 && query.length >= 2 && !searching && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-cream-100 rounded-xl shadow-lg p-4 text-center">
          <p className="font-body text-sm text-[#A89F91]">No results found</p>
        </div>
      )}
    </div>
  );
}
